import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { config } from '@/lib/config';
import type { Bucket } from '@/components/BucketBadge';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type TokenGetter = () => Promise<string | null>;

async function request<T>(
  getToken: TokenGetter,
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const token = await getToken();
  if (!token) throw new ApiError('Not authenticated', 401);

  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let detail = res.statusText;
    try {
      const data = await res.json();
      detail = (data?.message as string) ?? (data?.error as string) ?? detail;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(detail, res.status);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Bound API client using the signed-in user's ID token. */
export function useApi() {
  const { getToken } = useAuth();
  const get = useCallback(<T,>(path: string) => request<T>(getToken, 'GET', path), [getToken]);
  const post = useCallback(
    <T,>(path: string, body?: unknown) => request<T>(getToken, 'POST', path, body),
    [getToken],
  );
  return { get, post };
}

interface QueryState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/** Fetch-on-mount helper for GET endpoints. */
export function useApiQuery<T>(path: string | null): QueryState<T> & { reload: () => void } {
  const { get } = useApi();
  const [state, setState] = useState<QueryState<T>>({ data: null, loading: path !== null, error: null });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (path === null) return;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    get<T>(path)
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ data: null, loading: false, error: err instanceof Error ? err.message : String(err) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [path, get, nonce]);

  return { ...state, reload: () => setNonce((n) => n + 1) };
}

// Response shapes — mirror packages/pulse-api/src/handlers.ts.
export interface DashboardResponse {
  tenantId: string;
  counts: { Engaged: number; Watching: number; AtRisk: number; Slipping: number };
  scoringPending?: boolean;
  synced?: { members: number; events: number };
  lastSync?: { status: string; startedAt: string; endedAt?: string } | null;
  trend: Array<{ date: string; [bucket: string]: number | string }>;
  stats?: {
    scored: number;
    signals: {
      eventsAndEmail: number;
      emailOnly: number;
      eventsOnly: number;
      neither: number;
      recencyUnknown: number;
    };
    computedAt: string;
  } | null;
  note?: string;
}

export interface MemberSummary {
  profileId: string;
  legalName?: string;
  status?: string;
  tenureBucket?: 'Year1' | 'Years2to5' | 'Years6Plus' | string;
  member?: boolean;
  orgInd?: boolean;
  city?: string;
  state?: string;
  bucket?: 'Engaged' | 'Watching' | 'AtRisk' | 'Slipping' | null;
  score?: number | null;
  recencyDays?: number | null;
}

export interface MembersResponse {
  items: MemberSummary[];
  nextCursor?: string;
  tenantId: string;
  note?: string;
}

export interface SyncRun {
  startedAt: string;
  endedAt?: string;
  status: string;
  mode?: string;
  results?: Array<{ resource: string; fetched: number; written: number; errors: string[] }>;
}

export interface BriefRow {
  profileId: string;
  legalName?: string;
  bucket: 'Engaged' | 'Watching' | 'AtRisk' | 'Slipping';
  score: number;
  assignedToUserId?: number | null;
  reasons: string[];
  suggestedAction: string;
}

export interface BriefResponse {
  tenantId: string;
  generatedAt: string;
  items: BriefRow[];
}

export interface SyncStatusResponse {
  tenantId: string;
  lastRun: SyncRun | null;
  note?: string;
}

export type SignalKey =
  | 'eventRegistration'
  | 'emailEngagement'
  | 'recencyDecay'
  | 'sponsorship'
  | 'phoneCall';

export interface SignalWeight {
  key: SignalKey;
  label: string;
  source: string;
  connected: boolean;
  weight: number;
}

export interface TenantConfigResponse {
  tenantId: string;
  weights: Record<SignalKey, number>;
  thresholds: { engaged: number; watching: number; atRisk: number };
  inactivityFlagMonths: number;
  signals: SignalWeight[];
  weightTotal: number;
}

export interface SignalBreakdown {
  key: SignalKey;
  label: string;
  component: number | null;
  weight: number;
  effectiveWeight: number;
  contribution: number;
  evidence: string;
  unavailable?: 'no-source' | 'unknown';
}

export interface ScoreComponents {
  recencyDays: number | null;
  frequency90d: number;
  frequency365d: number;
  tenureBucket: string;
  sponsorshipTouches365d: number;
  joinedAtInferred?: boolean;
  emailOpens365d?: number;
  emailClicks365d?: number;
}

export interface MemberDetailResponse {
  tenantId: string;
  member: Record<string, unknown> & {
    profileId: string;
    legalName?: string;
    status?: string;
    city?: string;
    state?: string;
    email?: string;
    tenureBucket?: string;
    joinedAt?: string;
    member?: boolean;
  };
  score: {
    score: number;
    bucket: Bucket;
    computedAt: string;
    cohortFloorApplied: boolean;
    inactivityFlagApplied: boolean;
    touchpointConfidence: 'high' | 'low';
    components?: ScoreComponents;
    breakdown: SignalBreakdown[];
  } | null;
  history: {
    registrations: Array<{ eventName: string; registeredAt: string; amount?: number }>;
    registrationCount: number;
    campaigns: Array<{
      campaignName: string;
      sentDate: string;
      opens: number;
      clicks: number;
      matchType: string;
    }>;
    campaignCount: number;
    emailTotals: { opens: number; clicks: number; proxyOpens: number; campaignsEngaged: number };
  };
}
